/**
 * Edit Venue Profile page component.
 * Allows venue owners and admins to update venue information and manage admins.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { trpc } from '../utils/trpc';
import style from '../styles/VenueForm.module.css';
import VenueBannerUpload from '../components/VenueBannerUpload';
import VenueGalleryUpload from '../components/VenueGalleryUpload';
import { useAlertStore } from '../utils/alert';
import { useAuth } from '../contexts/AuthContext';

/**
 * VenueEditor component for viewing and editing a specific venue.
 * @returns The edit venue page with form and admin management.
 */
function VenueEditor() {
  const navigate = useNavigate();
  const { venueId } = useParams<{ venueId: string }>();
  const { session, isAuthenticated, isLoading } = useAuth();

  // Form state - venue fields
  const [venueName, setVenueName] = useState('');
  const [address, setAddress] = useState('');
  const [openTime, setOpenTime] = useState('');
  const [closeTime, setCloseTime] = useState('');
  const [maxCapacity, setMaxCapacity] = useState('');
  const [bannerUrl, setBannerUrl] = useState<string | null>(null);

  // Admin management state
  const [newAdminId, setNewAdminId] = useState(''); // User ID to add

  // Validate UUID format
  const isValidUuid = !!(
    venueId &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(venueId)
  );

  /** Fetch venue details */
  const venueQuery = trpc.venue.getById.useQuery(
    { venue_id: venueId! },
    {
      enabled: !!venueId && isValidUuid && isAuthenticated,
      retry: false,
    },
  );

  /** Fetch venue admins (only for owners) */
  const adminsQuery = trpc.venue.getAdmins.useQuery(
    { venue_id: venueId! },
    {
      enabled: !!venueId && isValidUuid && isAuthenticated,
      retry: false,
    },
  );

  /** Update venue mutation */
  const updateVenue = trpc.venue.update.useMutation({
    onSuccess: (data) => {
      if (data.updated) {
        useAlertStore.getState().showAlert({
          header: 'Success',
          body: 'Venue updated successfully',
          button: 'Ok',
        });
        navigate('/venue-owner/profile');
        venueQuery.refetch();
      } else {
        useAlertStore.getState().showAlert({
          header: 'Error',
          body: 'Error updating venue',
          button: 'Ok',
        });
      }
    },
    onError: (_error) => {
      useAlertStore.getState().showAlert({
        header: 'Error',
        body: 'Error updating venue',
        button: 'Ok',
      });
    },
  });

  /** Add admin mutation */
  const addAdmin = trpc.venue.addAdmin.useMutation({
    onSuccess: () => {
      useAlertStore.getState().showAlert({
        header: 'Success',
        body: 'Admin added successfully',
        button: 'Ok',
      });
      setNewAdminId('');
      adminsQuery.refetch();
    },
    onError: (_error) => {
      useAlertStore.getState().showAlert({
        header: 'Error',
        body: 'Error adding admin',
        button: 'Ok',
      });
    },
  });

  /** Remove admin mutation */
  const removeAdmin = trpc.venue.removeAdmin.useMutation({
    onSuccess: () => {
      useAlertStore.getState().showAlert({
        header: 'Success',
        body: 'Admin removed successfuly',
        button: 'Ok',
      });
      adminsQuery.refetch();
    },
    onError: (_error) => {
      useAlertStore.getState().showAlert({
        header: 'Error',
        body: 'Error removing admin',
        button: 'Ok',
      });
    },
  });

  /** Redirect unauthenticated users */
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [isLoading, isAuthenticated, navigate]);

  /**
   * Redirect if no venueId
   */
  useEffect(() => {
    if (!venueId) {
      navigate('/dashboard');
    }
  }, [venueId, navigate]);

  /**
   * Populate form when venue data loads
   */
  useEffect(() => {
    if (venueQuery.data) {
      queueMicrotask(() => {
        setVenueName(venueQuery.data.venue_name || '');
        setAddress(venueQuery.data.address || '');
        setOpenTime(venueQuery.data.open_time || '');
        setCloseTime(venueQuery.data.close_time || '');
        setMaxCapacity(venueQuery.data.max_capacity?.toString() || '');
        setBannerUrl(venueQuery.data.banner_url || null);
      });
    }
  }, [venueQuery.data]);

  /**
   * Handle venue fields submission
   */
  const handleVenueSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!venueId) return;

    const updates: {
      venue_id: string;
      venue_name?: string;
      address?: string;
      open_time?: string;
      close_time?: string;
      max_capacity?: number;
      banner_url?: string;
    } = { venue_id: venueId };

    if (venueName.trim()) updates.venue_name = venueName.trim();
    if (address.trim()) updates.address = address.trim();
    if (openTime.trim()) updates.open_time = openTime.trim();
    if (closeTime.trim()) updates.close_time = closeTime.trim();
    if (maxCapacity.trim()) {
      const parsedCapacity = parseInt(maxCapacity);
      if (!isNaN(parsedCapacity) && parsedCapacity > 0) {
        updates.max_capacity = parsedCapacity;
      }
    }
    if (bannerUrl) updates.banner_url = bannerUrl;

    updateVenue.mutate(updates);
  };

  /**
   * Handle adding an admin
   */
  const handleAddAdmin = (e: React.FormEvent) => {
    e.preventDefault();
    if (!venueId || !newAdminId.trim()) return;
    addAdmin.mutate({ venue_id: venueId, user_id: newAdminId.trim() });
  };

  if (venueId && !isValidUuid) {
    return (
      <div className={style.container}>
        <div className={style.formContainer}>
          <h1 style={{ color: 'red' }}>Invalid Venue ID</h1>
          <p>The venue ID "{venueId}" is not valid.</p>
          <button onClick={() => navigate('/dashboard')} className={style.button}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!venueId || !session) return null;

  if (venueQuery.isLoading) {
    return (
      <div className={style.container}>
        <div className={style.formContainer}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const isOwner = session.user.id === venueQuery.data?.owner_id;

  return (
    <div className={style.container}>
      <div className={style.formContainer}>
        <h1>Edit Venue</h1>
        <h2>{venueQuery.data?.venue_name}</h2>

        {venueQuery.error && (
          <p style={{ color: 'red', marginBottom: '1rem' }}>{venueQuery.error.message}</p>
        )}

        <VenueBannerUpload
          currentUrl={bannerUrl}
          venueId={venueId}
          onUploadComplete={(url) => {
            setBannerUrl(url);
            updateVenue.mutate({ venue_id: venueId, banner_url: url });
          }}
        />

        <form onSubmit={handleVenueSubmit} className={style.form}>
          <div className={style.fieldContainer}>
            <div className={style.fieldWithLabel}>
              <label htmlFor="venueName">Venue Name</label>
              <input
                id="venueName"
                type="text"
                value={venueName}
                onChange={(e) => setVenueName(e.target.value)}
                className={style.field}
              />
            </div>
          </div>

          <div className={style.fieldContainer}>
            <div className={style.fieldWithLabel}>
              <label htmlFor="address">Address</label>
              <input
                id="address"
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className={style.field}
              />
            </div>
          </div>

          <div className={style.fieldContainer}>
            <div className={style.fieldWithLabel}>
              <label htmlFor="openTime">Opens At</label>
              <input
                id="openTime"
                type="time"
                value={openTime}
                onChange={(e) => setOpenTime(e.target.value)}
                className={style.field}
              />
            </div>
          </div>

          <div className={style.fieldContainer}>
            <div className={style.fieldWithLabel}>
              <label htmlFor="closeTime">Closes At</label>
              <input
                id="closeTime"
                type="time"
                value={closeTime}
                onChange={(e) => setCloseTime(e.target.value)}
                className={style.field}
              />
            </div>
          </div>

          <div className={style.fieldContainer}>
            <div className={style.fieldWithLabel}>
              <label htmlFor="maxCapacity">Capacity</label>
              <input
                id="maxCapacity"
                type="number"
                min="1"
                value={maxCapacity}
                onChange={(e) => setMaxCapacity(e.target.value)}
                className={style.field}
              />
            </div>
          </div>

          <div className={style.buttonContainer}>
            <button
              type="button"
              onClick={() => navigate('/venue-owner/profile')}
              className={`${style.button} ${style.cancelButton}`}
            >
              Back
            </button>
            <button
              type="submit"
              disabled={updateVenue.isPending}
              className={`${style.button} ${style.submitButton}`}
            >
              {updateVenue.isPending ? 'Updating...' : 'Update Venue'}
            </button>
          </div>
        </form>

        <VenueGalleryUpload venueId={venueId} />

        {/* Admin Management Section (Owner Only) */}
        {isOwner && (
          <div
            style={{
              width: '100%',
              marginTop: '2rem',
              borderTop: '2px solid #2D4F2B',
              paddingTop: '2rem',
            }}
          >
            <h2>Manage Admins</h2>
            <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
              Admins can update venue details but cannot delete the venue or manage other
              admins.
            </p>

            <form
              onSubmit={handleAddAdmin}
              className={style.form}
              style={{ marginBottom: '1.5rem' }}
            >
              <div className={style.fieldContainer}>
                <div className={style.fieldWithLabel}>
                  <label htmlFor="adminId">Add Admin ID</label>
                  <input
                    id="adminId"
                    type="text"
                    placeholder="User UUID"
                    value={newAdminId}
                    onChange={(e) => setNewAdminId(e.target.value)}
                    className={style.field}
                  />
                  <button
                    type="submit"
                    disabled={addAdmin.isPending}
                    className={`${style.button} ${style.submitButton}`}
                    style={{ padding: '0.5rem 1rem', marginLeft: '1rem' }}
                  >
                    Add
                  </button>
                </div>
              </div>
            </form>

            {adminsQuery.isLoading ? (
              <p>Loading admins...</p>
            ) : adminsQuery.data && adminsQuery.data.length > 0 ? (
              <ul style={{ listStyle: 'none', padding: 0, width: '100%' }}>
                {adminsQuery.data.map((admin) => (
                  <li
                    key={admin.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.5rem',
                      borderBottom: '1px solid #ccc',
                      background: 'rgba(232, 229, 193, 0.3)',
                      borderRadius: '8px',
                      marginBottom: '0.5rem',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'monospace',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {admin.user_id}
                    </span>
                    <button
                      onClick={() =>
                        removeAdmin.mutate({
                          venue_id: venueId,
                          user_id: admin.user_id,
                        })
                      }
                      disabled={removeAdmin.isPending}
                      style={{
                        background: '#dc3545',
                        color: 'white',
                        padding: '0.25rem 0.75rem',
                        border: 'none',
                        borderRadius: '20px',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                      }}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p style={{ color: '#666', fontStyle: 'italic' }}>
                No admins assigned to this venue.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default VenueEditor;
